import React from "react";
import PageHeader from "../../common/PageHeader";
import CommonDialog from "../../common/CommonDialog";
import { GlobalContext } from "../../context/GlobalContext";
import {
  AutoFixHigh,
  AddOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";

import {
  formatPromptLibraryRange,
  parseListResponse,
  shouldShowPagination,
} from "../../user/promptLibrary/promptLibraryApi";
import { VIDEO_STUDIO_USE_CASE_FILTER_OPTIONS } from "../../user/videoStudio/categories";

const PAGE_SIZE = 24;

function PromptListPager({ pagination, page, onPageChange, loading, rangeLabel }) {
  if (!shouldShowPagination(pagination, PAGE_SIZE)) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        justifyContent: "space-between",
        gap: 2,
        py: 1.5,
        px: 2,
        mb: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      {rangeLabel ? (
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {rangeLabel}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          Page {page} / {pagination.total_pages}
        </Typography>
      )}
      <Pagination
        count={pagination.total_pages}
        page={page}
        onChange={(_, p) => onPageChange(p)}
        color="primary"
        shape="rounded"
        disabled={loading}
        showFirstButton
        showLastButton
        siblingCount={1}
        boundaryCount={1}
        sx={{ alignSelf: { xs: "center", sm: "flex-end" } }}
      />
    </Box>
  );
}

const USE_CASES = [
  { id: "", label: "All" },
  ...VIDEO_STUDIO_USE_CASE_FILTER_OPTIONS,
  { id: "lifestyle", label: "Lifestyle / Action" },
];

const MODEL_PRESETS = [
  "kling-3.0",
  "kling-o3-standard",
  "kling-o3-pro",
];

function parseCsv(s) {
  return String(s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function PromptCard({ item, onEdit, onDelete, lang }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2.5, height: "100%" }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Stack spacing={1.25}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={900} noWrap>
                {item.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                #{item.id} {item.use_case ? `· ${item.use_case}` : ""}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => onEdit(item)}>
                  <EditOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={() => onDelete(item)}>
                  <DeleteOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {(item.model_ids || []).slice(0, 6).map((m) => (
              <Chip key={m} size="small" label={m} variant="outlined" />
            ))}
            {(item.tags || []).slice(0, 6).map((t) => (
              <Chip key={t} size="small" label={t} color="secondary" variant="outlined" />
            ))}
          </Stack>

          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              bgcolor: "rgba(0,0,0,0.03)",
              p: 1.25,
              borderRadius: 2,
              maxHeight: 180,
              overflow: "auto",
            }}
          >
            {item.prompt}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function PromptLibraryAdmin({ lang }) {
  const { hitAxios } = React.useContext(GlobalContext);
  const [items, setItems] = React.useState([]);
  const [pagination, setPagination] = React.useState(null);
  const [categories, setCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const [q, setQ] = React.useState("");
  const [qDebounced, setQDebounced] = React.useState("");
  const [useCase, setUseCase] = React.useState("");
  const [modelId, setModelId] = React.useState("");
  const [categoryId, setCategoryId] = React.useState("");
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 400);
    return () => clearTimeout(t);
  }, [q]);

  React.useEffect(() => {
    setPage(1);
  }, [qDebounced, useCase, modelId, categoryId]);

  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [draft, setDraft] = React.useState({
    title: "",
    prompt: "",
    use_case: "",
    category_id: "",
    model_ids_csv: "",
    tags_csv: "",
  });

  const loadCategories = React.useCallback(async () => {
    const res = await hitAxios({ path: "/api/admin/get_t_care", post: false, admin: false });
    if (res?.data?.success) setCategories(res.data.data || []);
  }, [hitAxios]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (qDebounced) params.set("q", qDebounced);
    if (useCase) params.set("use_case", useCase);
    if (modelId) params.set("model_id", modelId);
    if (categoryId) params.set("category_id", categoryId);
    params.set("page", String(page));
    params.set("limit", String(PAGE_SIZE));
    const res = await hitAxios({
      path: `/api/admin/prompt_library?${params.toString()}`,
      post: false,
      admin: true,
      showLoading: false,
      showSnackbar: false,
    });
    if (!res?.data?.success) {
      setItems([]);
      setPagination(null);
      setError(res?.data?.msg || "Failed to load prompt library.");
      setLoading(false);
      return;
    }
    const { items: rows, pagination: pag } = parseListResponse(res.data.data, {
      page,
      pageSize: PAGE_SIZE,
    });
    if (pag.total_pages > 0 && page > pag.total_pages) {
      setPage(pag.total_pages);
      setLoading(false);
      return;
    }
    setItems(rows);
    setPagination(pag);
    setLoading(false);
  }, [hitAxios, qDebounced, useCase, modelId, categoryId, page]);

  const rangeLabel = React.useMemo(
    () => formatPromptLibraryRange(pagination, lang),
    [pagination, lang],
  );

  const handlePageChange = React.useCallback((p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  React.useEffect(() => {
    loadCategories();
    load();
  }, [loadCategories, load]);

  const openNew = () => {
    setEditing(null);
    setDraft({
      title: "",
      prompt: "",
      use_case: "",
      category_id: "",
      model_ids_csv: "",
      tags_csv: "",
    });
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setDraft({
      title: item.title || "",
      prompt: item.prompt || "",
      use_case: item.use_case || "",
      category_id: item.category_id ? String(item.category_id) : "",
      model_ids_csv: (item.model_ids || []).join(", "),
      tags_csv: (item.tags || []).join(", "),
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...(editing?.id ? { id: editing.id } : {}),
      title: draft.title,
      prompt: draft.prompt,
      use_case: draft.use_case || null,
      category_id: draft.category_id ? Number(draft.category_id) : null,
      model_ids: parseCsv(draft.model_ids_csv),
      tags: parseCsv(draft.tags_csv),
    };
    const res = await hitAxios({
      path: "/api/admin/prompt_library_upsert",
      post: true,
      admin: true,
      obj: payload,
      showLoading: false,
      showSnackbar: false,
    });
    if (res?.data?.success) {
      setOpen(false);
      await load();
    }
  };

  const del = async (item) => {
    const res = await hitAxios({
      path: "/api/admin/prompt_library_delete",
      post: true,
      admin: true,
      obj: { id: item.id },
      showLoading: false,
      showSnackbar: false,
    });
    if (res?.data?.success) {
      await load();
    }
  };

  return (
    <Box>
      <PageHeader
        icon={AutoFixHigh}
        title={lang?.adminPromptLibraryTitle || "Prompt Library"}
        subtitle={
          lang?.adminPromptLibrarySub ||
          "Curate prompts users can browse by category, model, and use-case."
        }
        primaryAction={
          <Button size="large" variant="contained" startIcon={<AddOutlined />} onClick={openNew}>
            {lang?.add || "Add"}
          </Button>
        }
      />

      <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-end" }}>
            <TextField
              size="small"
              label={lang?.search || "Search"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="title, prompt, tags…"
              sx={{ flex: 1, minWidth: 240 }}
            />
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{lang?.useCase || "Use-case"}</InputLabel>
              <Select label={lang?.useCase || "Use-case"} value={useCase} onChange={(e) => setUseCase(e.target.value)}>
                {USE_CASES.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{lang?.model || "Model"}</InputLabel>
              <Select label={lang?.model || "Model"} value={modelId} onChange={(e) => setModelId(e.target.value)}>
                <MenuItem value="">{lang?.all || "All"}</MenuItem>
                {MODEL_PRESETS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{lang?.category || "Category"}</InputLabel>
              <Select
                label={lang?.category || "Category"}
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <MenuItem value="">{lang?.all || "All"}</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name || `Category #${c.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={load} disabled={loading} sx={{ fontWeight: 800 }}>
              {lang?.refresh || "Refresh"}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <PromptListPager
        pagination={pagination}
        page={page}
        onPageChange={handlePageChange}
        loading={loading}
        rangeLabel={rangeLabel}
      />

      {loading && items.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}

      <Grid container spacing={2} sx={{ opacity: loading ? 0.6 : 1 }}>
        {items.map((it) => (
          <Grid item xs={12} md={6} lg={4} key={it.id}>
            <PromptCard item={it} onEdit={openEdit} onDelete={del} lang={lang} />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 3 }}>
        <PromptListPager
          pagination={pagination}
          page={page}
          onPageChange={handlePageChange}
          loading={loading}
          rangeLabel={rangeLabel}
        />
      </Box>

      <CommonDialog
        open={open}
        setOpen={setOpen}
        title={editing ? (lang?.edit || "Edit") : lang?.add || "Add"}
        actionText={lang?.save || "Save"}
        actionIcon={<SaveOutlined />}
        onAction={save}
        maxWidth="md"
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label={lang?.title || "Title"}
            value={draft.title}
            onChange={(e) => setDraft((p) => ({ ...p, title: e.target.value }))}
            fullWidth
          />

          <TextField
            label={lang?.prompt || "Prompt"}
            value={draft.prompt}
            onChange={(e) => setDraft((p) => ({ ...p, prompt: e.target.value }))}
            multiline
            minRows={8}
            fullWidth
          />

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>{lang?.useCase || "Use-case"}</InputLabel>
              <Select
                label={lang?.useCase || "Use-case"}
                value={draft.use_case}
                onChange={(e) => setDraft((p) => ({ ...p, use_case: e.target.value }))}
              >
                {USE_CASES.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>{lang?.category || "Category"}</InputLabel>
              <Select
                label={lang?.category || "Category"}
                value={draft.category_id}
                onChange={(e) => setDraft((p) => ({ ...p, category_id: e.target.value }))}
              >
                <MenuItem value="">{lang?.none || "None"}</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name || `Category #${c.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <TextField
            label={lang?.models || "Model ids (comma-separated)"}
            value={draft.model_ids_csv}
            onChange={(e) => setDraft((p) => ({ ...p, model_ids_csv: e.target.value }))}
            placeholder={MODEL_PRESETS.join(", ")}
            fullWidth
          />
          <TextField
            label={lang?.tags || "Tags (comma-separated)"}
            value={draft.tags_csv}
            onChange={(e) => setDraft((p) => ({ ...p, tags_csv: e.target.value }))}
            placeholder="avatar, product, lighting, camera, etc"
            fullWidth
          />
        </Stack>
      </CommonDialog>
    </Box>
  );
}

